import React, { Fragment, useMemo } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import get from 'lodash.get';

import { useSelectedQuantityContext, useSelectedVariantContext, useTreatmentSelectorUpdaterContext } from '../../../context/TreatmentSelectorContext';
import useGetWindowSize, { tabletWindowSizeMin } from '../../../../../hooks/useGetWindowSize';
import useLockViewportScrolling from '../../../../../hooks/useLockViewportScrolling';

import Quantity from './Quantity/Quantity';
import ChevronRight from '../../../../../components/_ui/_blocks/Icon/IconComponents/ChevronRight';
import Typography from '../../../../../components/_ui/_blocks/Typography/Typography';
import Icon from '../../../../../components/_ui/_blocks/Icon/Icon';

import styles from './RegularQuantitySelector.module.css';

const getLabel = (selectedVariant: ReturnType<typeof useSelectedVariantContext>) => {
    const customLabel = get(selectedVariant, 'public.treatmentSelector.variantLabel');

    if (customLabel) {
        return customLabel;
    }

    return selectedVariant.type === 'ODONLY' ? 'Quantity preference' : 'Select quantity';
}

interface LockViewportProps {
    open: boolean;
    windowSize: number;
}

const LockViewport = ({ open, windowSize }: LockViewportProps) => {
    useLockViewportScrolling(!!open && windowSize < tabletWindowSizeMin)
    return null;
}

const RegularQuantitySelector = () => {
    const selectedVariant = useSelectedVariantContext();
    const selectedQuantity = useSelectedQuantityContext();
    const { updateQuantity } = useTreatmentSelectorUpdaterContext();

    const windowSize = useGetWindowSize();

    const sortedQuantities = useMemo(() => (
        selectedVariant.quantities.sort((a, b) => a.quantity - b.quantity)
    ), [selectedVariant]);

    const handleChange = (value: number) => {
        updateQuantity(selectedVariant.quantities.find(({ quantity }) => quantity === value)!);
    };

    const isVariantInStock = selectedVariant.stock > 0;

    return (
        <Listbox as="div" value={selectedQuantity?.quantity} onChange={handleChange}>
            {({ open }) => (
                <Fragment>
                    <Listbox.Label className="type-emphasis type-090 leading-none">
                        {getLabel(selectedVariant)}
                    </Listbox.Label>
                    <div className={styles.wrapper} data-wrapper="true">
                        {selectedQuantity ? (
                            <Listbox.Button>
                                <Quantity
                                    quantity={selectedQuantity}
                                    isOutOfStock={!isVariantInStock || selectedQuantity.quantity > selectedVariant.stock}
                                />
                                <ChevronRight size="xsmall" className="flex-shrink-0" />
                            </Listbox.Button>
                        ) : null}
                        <Transition
                            show={open}
                            enterFrom={`${styles.dropDownTransition}`}
                            leaveTo={`${styles.dropDownTransition}`}
                            className={styles.dropDown}
                        >
                            <Transition.Child
                                enterFrom="transform translate-y-[100%] md:opacity-0"
                                enterTo="transform translate-y-[0] md:opacity-100"
                                leaveFrom="transform translate-y-[0] md:opacity-100"
                                leaveTo="transform translate-y-[100%] md:opacity-0"
                                className={styles.overlay}
                            >
                                {/*
                                    Wrapped in a query because if there are 2 Listbox buttons rendered,
                                    clicking the first button directly no longer closes the overlay for some
                                    reason.
                                */}
                                {windowSize < tabletWindowSizeMin ? (
                                    <div className="flex gap-100 justify-between items-center flex-grow-0 mb-100 md:hidden">
                                        <Typography as="div" typeset="emphasis" size="090" lineHeight="none">
                                            Quantity options
                                        </Typography>
                                        <Listbox.Button title="Close overlay">
                                            <Icon icon="cross-light" alt="" aria-hidden="true" />
                                        </Listbox.Button>
                                    </div>
                                ) : null}
                                <Listbox.Options static>
                                    {sortedQuantities.map((quantity) => {
                                        const isOutOfStock = !isVariantInStock || quantity.quantity > selectedVariant.stock;

                                        return (
                                            <Listbox.Option
                                                key={quantity.quantity}
                                                value={quantity.quantity}
                                                disabled={isOutOfStock}
                                            >
                                                <Quantity quantity={quantity} isOutOfStock={isOutOfStock} />
                                            </Listbox.Option>
                                        );
                                    })}
                                </Listbox.Options>
                            </Transition.Child>
                        </Transition>
                    </div>
                    <LockViewport open={open} windowSize={windowSize} />
                </Fragment>
            )}
        </Listbox>
    );
};

export default RegularQuantitySelector;
